/**
 * Live-subscribed absence data. Single-field `where` clauses only (no composite
 * indexes): we sort/filter client-side. Reads are gated by firestore.rules
 * (owner / approver / admin).
 */
import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { AbsenceRequest } from '@daaily/shared';

export function useMyAbsences(email: string | null): AbsenceRequest[] | null {
  const [items, setItems] = useState<AbsenceRequest[] | null>(null);
  useEffect(() => {
    if (!email) {
      setItems([]);
      return;
    }
    const q = query(collection(db, 'absenceRequests'), where('ownerEmail', '==', email.toLowerCase()));
    return onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => d.data() as AbsenceRequest);
        // By absence date, latest first (upcoming on top, then the past) —
        // Falco's feedback Oct 2026; was by submission time. Same start → newest submission first.
        list.sort((a, b) =>
          a.startDate !== b.startDate
            ? (a.startDate < b.startDate ? 1 : -1)
            : (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0),
        );
        setItems(list);
      },
      (err) => {
        console.error('[useMyAbsences] snapshot error:', err);
        setItems([]);
      },
    );
  }, [email]);
  return items;
}

/** Pending requests the caller can act on. `seeAll` (admin/HR) → every
 *  submitted request; otherwise → requests assigned to the caller as approver. */
export function usePendingApprovals(
  email: string | null,
  seeAll: boolean,
): AbsenceRequest[] | null {
  const [items, setItems] = useState<AbsenceRequest[] | null>(null);
  useEffect(() => {
    if (!email) {
      setItems([]);
      return;
    }
    const col = collection(db, 'absenceRequests');
    const q = seeAll
      ? query(col, where('status', '==', 'submitted'))
      : query(col, where('approverEmail', '==', email.toLowerCase()));
    return onSnapshot(
      q,
      (snap) => {
        let list = snap.docs.map((d) => d.data() as AbsenceRequest);
        if (!seeAll) list = list.filter((r) => r.status === 'submitted');
        list.sort((a, b) => (a.startDate < b.startDate ? -1 : 1));
        setItems(list);
      },
      (err) => {
        console.error('[usePendingApprovals] snapshot error:', err);
        setItems([]);
      },
    );
  }, [email, seeAll]);
  return items;
}
